import { enUS, ptBR, es, fr, de, it, ru } from "date-fns/locale";
import {
  format,
  parseISO,
  isBefore,
  startOfDay,
  differenceInMinutes,
  isValid,
} from "date-fns";

// Normalises a timestamp to one calendar date so the output does not depend on
// the runtime timezone. date-fns `format()` renders in the local timezone, so an
// instant such as "2026-06-24T23:00:00Z" formats as 24 Jun on a UTC server and
// 25 Jun in a UTC+1 browser, which breaks React hydration. Date-only strings
// keep their literal date; instants are read in UTC. The returned Date is built
// at local midnight so date-fns always renders that exact day.
const getCalendarDate = (timeStamp) => {
  if (!timeStamp) return null;
  if (typeof timeStamp === "string") {
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(timeStamp.trim());
    if (dateOnly) {
      return new Date(
        Number(dateOnly[1]),
        Number(dateOnly[2]) - 1,
        Number(dateOnly[3]),
      );
    }
  }
  const parsed =
    typeof timeStamp === "string" ? parseISO(timeStamp) : timeStamp;
  if (!(parsed instanceof Date) || !isValid(parsed)) return null;
  return new Date(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate(),
  );
};

const getSimpleDate = (timeStamp, locale) => {
  try {
    const dateObj = getCalendarDate(timeStamp);
    if (!dateObj) return "";
    return safeFormat(dateObj, DATE_FORMATS.DATE_SHORT, {
      locale: getDateLocale(locale),
    });
  } catch {
    return "";
  }
};

export default getSimpleDate;

export const getFormattedMins = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
};

// date-fns locale map — maps Next.js locale strings to date-fns locale objects.
// Used by components that call date-fns format() with locale-aware output.
export const dateLocales = {
  en: enUS,
  "pt-br": ptBR,
  "es-la": es,
  it,
  fr,
  de,
  ru,
};

// Returns the date-fns locale for a Next.js locale string, falling back to enUS.
export const getDateLocale = (locale) => {
  if (locale?.startsWith("es")) return es;
  if (locale?.startsWith("pt")) return ptBR;
  if (locale?.startsWith("fr")) return fr;
  if (locale?.startsWith("de")) return de;
  if (locale?.startsWith("it")) return it;
  if (locale?.startsWith("ru")) return ru;
  return dateLocales[locale] ?? enUS;
};

// BCP-47 locale codes used by Intl.DateTimeFormat for the supported Next.js locales.
const intlLocaleCodes = {
  en: "en-US",
  "pt-br": "pt-BR",
  "es-la": "es",
  it: "it-IT",
  fr: "fr-FR",
  de: "de-DE",
  ru: "ru-RU",
};

// Returns the BCP-47 locale code for a Next.js locale string, falling back to the locale itself.
const getIntlLocale = (locale) => intlLocaleCodes[locale] ?? locale;

// Formats a date in a specific IANA timezone using Intl.DateTimeFormat.
// `options` are the standard Intl.DateTimeFormat options (e.g., { hour: "2-digit", minute: "2-digit" }).
export const formatInTimeZone = (date, timeZone, options, locale) => {
  if (!date || !timeZone) return "";
  try {
    const dateObj = typeof date === "string" ? parseISO(date) : date;
    if (!isValid(dateObj)) return "";
    return new Intl.DateTimeFormat(getIntlLocale(locale), {
      ...options,
      timeZone,
    }).format(dateObj);
  } catch {
    return "";
  }
};

// Formats an instant as a localized full date and 24-hour time. English uses
// day-first UK order ("Thursday 19 November 2026") to match the site's copy;
// other locales keep their native date order.
export const getFormattedDateTime = (date, locale, timeZone) => {
  const normalizedLocale = locale || "en";
  const isEnglish = normalizedLocale.startsWith("en");
  const formattedDate = formatInTimeZone(
    date,
    timeZone,
    {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    },
    isEnglish ? "en-GB" : normalizedLocale,
  );

  return {
    date: isEnglish ? formattedDate.replace(",", "") : formattedDate,
    time: formatInTimeZone(
      date,
      timeZone,
      { hour: "2-digit", minute: "2-digit", hour12: false },
      normalizedLocale,
    ),
  };
};

// Returns the ISO date string (yyyy-MM-dd) for a given instant in the specified timezone.
// Used for grouping slots by day in the user's selected timezone.
export const getTimeZoneDateISO = (date, timeZone) => {
  if (!date || !timeZone) return "";
  try {
    const dateObj = typeof date === "string" ? parseISO(date) : date;
    if (!isValid(dateObj)) return "";
    const parts = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(dateObj);
    const year = parts.find((p) => p.type === "year")?.value;
    const month = parts.find((p) => p.type === "month")?.value;
    const day = parts.find((p) => p.type === "day")?.value;
    if (!year || !month || !day) return "";
    return `${year}-${month}-${day}`;
  } catch {
    return "";
  }
};

// Safe Wrappers for date-fns
export const safeParseISO = (dateString) => {
  if (!dateString) return null;
  try {
    const date = parseISO(dateString);
    return isValid(date) ? date : null;
  } catch {
    return null;
  }
};

export const safeFormat = (date, formatString, options = {}) => {
  if (!date) return "";
  try {
    const dateObj = typeof date === "string" ? parseISO(date) : date;
    if (!isValid(dateObj)) return "";
    return format(dateObj, formatString, options);
  } catch {
    return "";
  }
};

export const safeIsBefore = (date, dateToCompare) => {
  try {
    const d1 = typeof date === "string" ? parseISO(date) : date;
    const d2 =
      typeof dateToCompare === "string"
        ? parseISO(dateToCompare)
        : dateToCompare;
    if (!isValid(d1) || !isValid(d2)) return false;
    return isBefore(d1, d2);
  } catch {
    return false;
  }
};

export const safeStartOfDay = (date) => {
  try {
    const dateObj = typeof date === "string" ? parseISO(date) : date;
    if (!isValid(dateObj)) return null;
    return startOfDay(dateObj);
  } catch {
    return null;
  }
};

export const safeDifferenceInMinutes = (dateLeft, dateRight) => {
  try {
    const dLeft = typeof dateLeft === "string" ? parseISO(dateLeft) : dateLeft;
    const dRight =
      typeof dateRight === "string" ? parseISO(dateRight) : dateRight;
    if (!isValid(dLeft) || !isValid(dRight)) return 0;
    return differenceInMinutes(dLeft, dRight);
  } catch {
    return 0;
  }
};

// Returns a localized "period of month year" phrase for a given date.
// Day 1-10 → early, 11-20 → middle, 21+ → late.
// Returns { periodKey, month, year } or null for invalid dates.
export const getMonthPeriod = (date, locale) => {
  if (!date) return null;
  const dateObj = safeParseISO(date);
  if (!dateObj) return null;

  const day = dateObj.getDate();
  let periodKey;
  if (day <= 10) {
    periodKey = "LBL_DATE_EARLY";
  } else if (day <= 20) {
    periodKey = "LBL_DATE_MIDDLE";
  } else {
    periodKey = "LBL_DATE_LATE";
  }

  try {
    const parts = new Intl.DateTimeFormat(getIntlLocale(locale), {
      month: "long",
      year: "numeric",
    }).formatToParts(dateObj);
    const month = parts.find((p) => p.type === "month")?.value;
    const year = parts.find((p) => p.type === "year")?.value;
    if (!month || !year) return null;
    return { periodKey, month, year };
  } catch {
    return null;
  }
};

export const DATE_FORMATS = {
  FULL_DATE_WITH_DAY: "EEEE, MMMM d, yyyy", // e.g. Monday, May 18, 2026
  DATE_WITH_MONTH_YEAR: "MMMM yyyy", // e.g. May 2026
  DATE_WITH_DAY_YEAR: "MMMM d, yyyy", // e.g. May 18, 2026
  DATE_SHORT: "d MMM yyyy", // e.g. 18 May 2026
  SHORT_DATE_DAY: "EEE d", // e.g. Mon 18
  DATE_ISO: "yyyy-MM-dd", // e.g. 2026-05-18
  TIME_24H: "HH:mm", // e.g. 14:00
  TIME_12H: "h:mm a", // e.g. 2:00 PM
  TIME_12H_SHORT: "h a", // e.g. 2 PM
  DATETIME_FULL_12H: "MMMM d, yyyy h:mm a", // e.g. May 18, 2026 2:00 PM
};

export const getFormattedSlotDate = (startTime, locale, timeZone) => {
  if (timeZone) {
    return formatInTimeZone(
      startTime,
      timeZone,
      {
        weekday: "long",
        month: "long",
        day: "numeric",
        year: "numeric",
      },
      locale,
    );
  }
  return safeFormat(startTime, DATE_FORMATS.FULL_DATE_WITH_DAY, {
    locale: getDateLocale(locale),
  });
};

export const getFormattedSlotTime = (startTime, locale, timeZone) => {
  if (timeZone) {
    return formatInTimeZone(
      startTime,
      timeZone,
      { hour: "2-digit", minute: "2-digit", hour12: false },
      locale,
    );
  }
  return safeFormat(startTime, DATE_FORMATS.TIME_24H, {
    locale: getDateLocale(locale),
  });
};

export const getFormattedSlotTime12h = (startTime, locale, timeZone) => {
  if (timeZone) {
    return formatInTimeZone(
      startTime,
      timeZone,
      { hour: "numeric", minute: "2-digit", hour12: true },
      locale,
    );
  }
  return safeFormat(startTime, DATE_FORMATS.TIME_12H, {
    locale: getDateLocale(locale),
  });
};

export const getUserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    // Fallback to UTC if detection fails or is unsupported
    return "UTC";
  }
};
